import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Grid } from '@mui/material'
import React, { useCallback, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { SettingsBox } from 'src/Elements/Data'
import SettingsFormContainer from 'src/Libs/form/components/SettingsFormContainer'
import { NumberField } from 'src/Libs/inputs/react-hook-form'
import { UnitTypes } from 'src/Libs/types'
import useGridAndSystemProtectionMutation from '../hooks/use-grid-and-system-protection-mutation'
import useSyncFormWithValues from '../hooks/use-sync-form-with-values'
import { FUNCTION_NUMBER } from '../types/common'
import {
  RestartConditionsFrequencyModel,
  RestartConditionsGradientModel,
  RestartConditionsSettingsModel,
  RestartConditionsTimeModel,
  RestartConditionsVoltageModel,
} from '../types/grid-and-system-protection.model'
import { getRestartConditionsSettingsSchema, RestartConditionsSettingsForm } from '../types/schemas'
import { getChangedFieldStyle, isFieldDisabled } from '../utils'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'
import { useFarmContext } from 'src/Elements/Farm'
const NAME_SPACE = 'systemSettings.grid-and-system-protection-configuration.restart-conditions'

type Props = {
  restartConditionsSettingsModel: RestartConditionsSettingsModel
}

const RestartConditionsSettings: React.FC<Props> = ({ restartConditionsSettingsModel }) => {
  const { t } = useCustomTranslation()
  const defaultValues = useMemo(() => getDefaultValues(restartConditionsSettingsModel), [restartConditionsSettingsModel])
  const methods = useForm<RestartConditionsSettingsForm>({
    resolver: zodResolver(getRestartConditionsSettingsSchema(restartConditionsSettingsModel)),
    defaultValues,
    mode: 'onBlur',
  })
  useSyncFormWithValues(methods.reset, defaultValues)

  const { isLoading, updateGridAndSystemProtection } = useGridAndSystemProtectionMutation()

  const { restartConditionsVoltageSettings, restartConditionsFrequencySettings, restartConditionsTimeSettings, restartConditionsGradientSettings } =
    restartConditionsSettingsModel
  const noDataAvailable =
    !restartConditionsVoltageSettings && !restartConditionsFrequencySettings && !restartConditionsTimeSettings && !restartConditionsGradientSettings

  const onSubmit = useCallback(
    async (data: RestartConditionsSettingsForm): Promise<void> => {
      const { dirtyFields } = methods.formState

      try {
        // ? Target expects different API calls for each restartCondition subsetting, so we loop through the dirty fields and call the update function for each of them
        await Promise.all(
          Object.keys(dirtyFields).map(key => {
            return updateGridAndSystemProtection(
              { [key]: data[key as keyof RestartConditionsSettingsForm] },
              FUNCTION_NUMBER[key as keyof typeof FUNCTION_NUMBER]
            )
          })
        )
        methods.reset(data)
      } catch (error) {
        methods.reset()
      }
    },
    [methods, updateGridAndSystemProtection]
  )

  if (noDataAvailable) return null

  return (
    <FormProvider {...methods}>
      <Grid item xs={12}>
        <AccordionItem title={t(`${NAME_SPACE}.title`)} accordionDetailsSx={{ padding: 0 }}>
          <Box component="form" onSubmit={methods.handleSubmit(onSubmit)}>
            <SettingsFormContainer onReset={methods.reset} isLoading={isLoading}>
              {restartConditionsVoltageSettings && <VoltageSettings restartConditionsVoltageSettings={restartConditionsVoltageSettings} />}

              {restartConditionsFrequencySettings && <FrequencySettings restartConditionsFrequencySettings={restartConditionsFrequencySettings} />}

              {restartConditionsTimeSettings && <TimeSettings restartConditionsTimeSettings={restartConditionsTimeSettings} />}

              {restartConditionsGradientSettings && <GradientSettings restartConditionsGradientSettings={restartConditionsGradientSettings} />}
            </SettingsFormContainer>
          </Box>
        </AccordionItem>
      </Grid>
    </FormProvider>
  )
}

//#region VoltageSettings
const VoltageSettings: React.FC<{ restartConditionsVoltageSettings: RestartConditionsVoltageModel }> = ({ restartConditionsVoltageSettings }) => {
  const { t } = useCustomTranslation()
  const { isFarm, isFarmMasterSelected } = useFarmContext()
  const { hasInstallationWritingRights } = useInstallationAccessRightsContext()

  const canEdit = isFarm ? hasInstallationWritingRights && isFarmMasterSelected : hasInstallationWritingRights
  return (
    <>
      <SettingsBox
        title={t(`${NAME_SPACE}.voltage-limit-min.title`)}
        suffix={
          <Box sx={{ display: 'flex', gap: 2 }}>
            <NumberField
              name="restartConditionsVoltageSettings.lowerVoltageThresholdStandard"
              label={t(`${NAME_SPACE}.common.standard`)}
              suffix={UnitTypes.volt}
              disabled={!canEdit || isFieldDisabled(restartConditionsVoltageSettings.lowerVoltageThresholdStandard)}
              sx={getChangedFieldStyle(restartConditionsVoltageSettings.lowerVoltageThresholdStandard.changed)}
            />
            <NumberField
              name="restartConditionsVoltageSettings.lowerVoltageThresholdFault"
              label={t(`${NAME_SPACE}.common.fault`)}
              suffix={UnitTypes.volt}
              disabled={!canEdit || isFieldDisabled(restartConditionsVoltageSettings.lowerVoltageThresholdFault)}
              sx={getChangedFieldStyle(restartConditionsVoltageSettings.lowerVoltageThresholdFault.changed)}
            />
          </Box>
        }
      />
      <SettingsBox
        title={t(`${NAME_SPACE}.voltage-limit-max.title`)}
        suffix={
          <Box sx={{ display: 'flex', gap: 2 }}>
            <NumberField
              name="restartConditionsVoltageSettings.upperVoltageThresholdStandard"
              label={t(`${NAME_SPACE}.common.standard`)}
              suffix={UnitTypes.volt}
              disabled={!canEdit || isFieldDisabled(restartConditionsVoltageSettings.upperVoltageThresholdStandard)}
              sx={getChangedFieldStyle(restartConditionsVoltageSettings.upperVoltageThresholdStandard.changed)}
            />
            <NumberField
              name="restartConditionsVoltageSettings.upperVoltageThresholdFault"
              label={t(`${NAME_SPACE}.common.fault`)}
              suffix={UnitTypes.volt}
              disabled={!canEdit || isFieldDisabled(restartConditionsVoltageSettings.upperVoltageThresholdFault)}
              sx={getChangedFieldStyle(restartConditionsVoltageSettings.upperVoltageThresholdFault.changed)}
            />
          </Box>
        }
      />
    </>
  )
}
//#endregion

//#region FrequencySettings
const FrequencySettings: React.FC<{ restartConditionsFrequencySettings: RestartConditionsFrequencyModel }> = ({
  restartConditionsFrequencySettings,
}) => {
  const { t } = useCustomTranslation()
  const { isFarm, isFarmMasterSelected } = useFarmContext()
  const { hasInstallationWritingRights } = useInstallationAccessRightsContext()

  const canEdit = isFarm ? hasInstallationWritingRights && isFarmMasterSelected : hasInstallationWritingRights

  return (
    <>
      <SettingsBox
        title={t(`${NAME_SPACE}.frequency-limit-min.title`)}
        suffix={
          <Box sx={{ display: 'flex', gap: 2 }}>
            <NumberField
              name="restartConditionsFrequencySettings.lowerFrequencyThresholdStandard"
              label={t(`${NAME_SPACE}.common.standard`)}
              suffix={UnitTypes.frequency}
              disabled={!canEdit || isFieldDisabled(restartConditionsFrequencySettings.lowerFrequencyThresholdStandard)}
              sx={getChangedFieldStyle(restartConditionsFrequencySettings.lowerFrequencyThresholdStandard.changed)}
            />
            <NumberField
              name="restartConditionsFrequencySettings.lowerFrequencyThresholdFault"
              label={t(`${NAME_SPACE}.common.fault`)}
              suffix={UnitTypes.frequency}
              disabled={!canEdit || isFieldDisabled(restartConditionsFrequencySettings.lowerFrequencyThresholdFault)}
              sx={getChangedFieldStyle(restartConditionsFrequencySettings.lowerFrequencyThresholdFault.changed)}
            />
          </Box>
        }
      />
      <SettingsBox
        title={t(`${NAME_SPACE}.frequency-limit-max.title`)}
        suffix={
          <Box sx={{ display: 'flex', gap: 2 }}>
            <NumberField
              name="restartConditionsFrequencySettings.upperFrequencyThresholdStandard"
              label={t(`${NAME_SPACE}.common.standard`)}
              suffix={UnitTypes.frequency}
              disabled={!canEdit || isFieldDisabled(restartConditionsFrequencySettings.upperFrequencyThresholdStandard)}
              sx={getChangedFieldStyle(restartConditionsFrequencySettings.upperFrequencyThresholdStandard.changed)}
            />
            <NumberField
              name="restartConditionsFrequencySettings.upperFrequencyThresholdFault"
              label={t(`${NAME_SPACE}.common.fault`)}
              suffix={UnitTypes.frequency}
              disabled={!canEdit || isFieldDisabled(restartConditionsFrequencySettings.upperFrequencyThresholdFault)}
              sx={getChangedFieldStyle(restartConditionsFrequencySettings.upperFrequencyThresholdFault.changed)}
            />
          </Box>
        }
      />
    </>
  )
}
//#endregion

//#region TimeSettings
const TimeSettings: React.FC<{ restartConditionsTimeSettings: RestartConditionsTimeModel }> = ({ restartConditionsTimeSettings }) => {
  const { t } = useCustomTranslation()
  const { isFarm, isFarmMasterSelected } = useFarmContext()
  const { hasInstallationWritingRights } = useInstallationAccessRightsContext()

  const canEdit = isFarm ? hasInstallationWritingRights && isFarmMasterSelected : hasInstallationWritingRights

  return (
    <SettingsBox
      title={t(`${NAME_SPACE}.time.title`)}
      suffix={
        <Box sx={{ display: 'flex', gap: 2 }}>
          <NumberField
            name="restartConditionsTimeSettings.delayStandard"
            label={t(`${NAME_SPACE}.common.standard`)}
            suffix={UnitTypes.second}
            disabled={!canEdit || isFieldDisabled(restartConditionsTimeSettings.delayStandard)}
            sx={getChangedFieldStyle(restartConditionsTimeSettings.delayStandard.changed)}
          />
          <NumberField
            name="restartConditionsTimeSettings.delayFault"
            label={t(`${NAME_SPACE}.common.fault`)}
            suffix={UnitTypes.second}
            disabled={!canEdit || isFieldDisabled(restartConditionsTimeSettings.delayFault)}
            sx={getChangedFieldStyle(restartConditionsTimeSettings.delayFault.changed)}
          />
        </Box>
      }
    />
  )
}
//#endregion

//#region GradientSettings
const GradientSettings: React.FC<{ restartConditionsGradientSettings: RestartConditionsGradientModel }> = ({ restartConditionsGradientSettings }) => {
  const { t } = useCustomTranslation()
  const { isFarm, isFarmMasterSelected } = useFarmContext()
  const { hasInstallationWritingRights } = useInstallationAccessRightsContext()

  const canEdit = isFarm ? hasInstallationWritingRights && isFarmMasterSelected : hasInstallationWritingRights

  return (
    <SettingsBox
      title={t(`${NAME_SPACE}.gradient.title`)}
      suffix={
        <Box sx={{ display: 'flex', gap: 2 }}>
          <NumberField
            name="restartConditionsGradientSettings.gradientStandard"
            label={t(`${NAME_SPACE}.common.standard`)}
            suffix={UnitTypes.powerPerMinute}
            disabled={!canEdit || isFieldDisabled(restartConditionsGradientSettings.gradientStandard)}
            sx={getChangedFieldStyle(restartConditionsGradientSettings.gradientStandard.changed)}
          />
          <NumberField
            name="restartConditionsGradientSettings.gradientFault"
            label={t(`${NAME_SPACE}.common.fault`)}
            suffix={UnitTypes.powerPerMinute}
            disabled={!canEdit || isFieldDisabled(restartConditionsGradientSettings.gradientFault)}
            sx={getChangedFieldStyle(restartConditionsGradientSettings.gradientFault.changed)}
          />
        </Box>
      }
    />
  )
}
//#endregion

export default RestartConditionsSettings

const getDefaultValues = (data: RestartConditionsSettingsModel): RestartConditionsSettingsForm => {
  return {
    restartConditionsTimeSettings: data.restartConditionsTimeSettings
      ? {
          delayStandard: data.restartConditionsTimeSettings.delayStandard.value,
          delayFault: data.restartConditionsTimeSettings.delayFault.value,
        }
      : undefined,
    restartConditionsGradientSettings: data.restartConditionsGradientSettings
      ? {
          gradientStandard: data.restartConditionsGradientSettings.gradientStandard.value,
          gradientFault: data.restartConditionsGradientSettings.gradientFault.value,
        }
      : undefined,
    restartConditionsVoltageSettings: data.restartConditionsVoltageSettings
      ? {
          lowerVoltageThresholdStandard: data.restartConditionsVoltageSettings.lowerVoltageThresholdStandard.value,
          lowerVoltageThresholdFault: data.restartConditionsVoltageSettings.lowerVoltageThresholdFault.value,
          upperVoltageThresholdStandard: data.restartConditionsVoltageSettings.upperVoltageThresholdStandard.value,
          upperVoltageThresholdFault: data.restartConditionsVoltageSettings.upperVoltageThresholdFault.value,
        }
      : undefined,
    restartConditionsFrequencySettings: data.restartConditionsFrequencySettings
      ? {
          lowerFrequencyThresholdStandard: data.restartConditionsFrequencySettings.lowerFrequencyThresholdStandard.value,
          lowerFrequencyThresholdFault: data.restartConditionsFrequencySettings.lowerFrequencyThresholdFault.value,
          upperFrequencyThresholdStandard: data.restartConditionsFrequencySettings.upperFrequencyThresholdStandard.value,
          upperFrequencyThresholdFault: data.restartConditionsFrequencySettings.upperFrequencyThresholdFault.value,
        }
      : undefined,
  }
}
